import { LocalDate } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Form, constantField, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollmentid,
  evaluation_template_id,
  evaluation_template_question_id,
  evaluationid,
  evaluationsectionid,
  lessongroupid,
  questiontype,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import { questionsField } from "../../client/questionsField";
import { renderDeleteButton, renderSaveButton } from "../../client/savebutton";
import * as routes from "../../routes";

export function render(opts: {
  userCanEditTemplates: boolean;
  enrollmentid: enrollmentid;
  lessongroupid: lessongroupid;
  evaluation:
    | {
        tag: "new";
        allTemplates: {
          id: evaluation_template_id;
          label: string;
          questions:
            | {
                id: evaluation_template_question_id;
                evaluation_template_id: evaluation_template_id;
                label: string;
                questiontype: questiontype;
                ordering: number;
                select_options: string[];
              }[]
            | null;
        }[];
      }
    | {
        tag: "existing";
        templ: {
          id: evaluation_template_id;
          label: string;
        };
        questions: {
          id: evaluation_template_question_id;
          evaluation_template_id: evaluation_template_id;
          label: string;
          questiontype: questiontype;
          ordering: number;
          select_options: string[];
        }[];
        evalu: {
          id: evaluationid;
          finalized: LocalDate | null;
          evaluation_template_id: evaluation_template_id;
          sections:
            | {
                id: evaluationsectionid;
                evaluation_template_question_id: evaluation_template_question_id | null;
                label: string;
                text_answer: string | null;
                number_answer: number | null;
                boolean_answer: boolean | null;
                select_answer: string | null;
                multiselect_answers: string[];
                questiontype: questiontype;
                ordering: number;
              }[]
            | null;
        };
      };
}): HTMLElement | HTMLElement[] {
  if (opts.evaluation.tag === "existing") {
    const ev = opts.evaluation;
    const form = new Form({})
      .addField("id", [], () => constantField(ev.evalu.id))
      .addField("enrollmentid", [], () => constantField(opts.enrollmentid))
      .addField("sections", [], () => {
        return questionsField<{
          evaluationsectionid: evaluationsectionid;
          evaluation_template_question_id: evaluation_template_question_id | null;
        }>(
          (ev.evalu.sections || [])
            .orderBy((s) => s.ordering)
            .map((section, i) => {
              const foundQuestion = ev.questions.find(
                (q) => q.label === section.label,
              );
              return {
                ...section,
                evaluationsectionid: section.id,
                mandatory: false,
                select_options: foundQuestion?.select_options || [],
                ordering: i + 1,
              };
            }),
        );
      })
      .build();

    const finalizeEvaluationButton =
      ev.evalu.finalized === null ? (
        renderSaveButton({
          formS: form.s,
          save: async (evalu) => {
            await rpc(routes.saveEvaluationSpec, undefined, {
              id: evalu.id,
              enrollmentid: evalu.enrollmentid,
              template: ev.templ.id,
              sections: evalu.sections,
            });
            await rpc(routes.finalizeEvaluationSpec, undefined, {
              id: ev.evalu.id,
              enrollmentid: opts.enrollmentid,
            });
            return {
              tag: "success" as const,
              text: "Gelukt! We hebben een email in de wachtrij gezet om deze evaluatie naar de student te verzenden.",
            };
          },
          text: {
            normal: "Finaliseer evaluatie",
            success: "Gelukt!",
          },
          reload: true,
          timeout: 3000,
        })
      ) : (
        <span></span>
      );

    const deleteEvaluationButton = renderDeleteButton({
      label: "Verwijder evaluatie",
      confirmationLabel: "Ben je zeker dat je deze evaluatie volledig wilt verwijderen?",
      doDelete: () =>
        rpc(routes.deleteEvaluationSpec, undefined, { id: ev.evalu.id }),
      redirectTo: routes.lessongroup_generalRoute.link({
        lessongroupid: opts.lessongroupid,
      }),
    });

    return (
      <div>
        <div style={ev.evalu.finalized !== null ? "pointer-events: none;" : ""}>
          {form.render()}
        </div>
        <section style="text-align: right">{finalizeEvaluationButton}</section>
        {ev.evalu.finalized === null ? (
          <section style="text-align: right">
            {deleteEvaluationButton}
            {renderSaveButton({
              formS: form.s,
              save: async (evalu) => {
                return rpc(routes.saveEvaluationSpec, undefined, {
                  id: evalu.id,
                  enrollmentid: evalu.enrollmentid,
                  template: ev.templ.id,
                  sections: evalu.sections,
                });
              },
              reload: true,
            })}
          </section>
        ) : (
          <section style="text-align: right">
            {renderSaveButton({
              formS: form.s,
              text: {
                normal: "Heropen evalutie",
              },
              icon: FA.icon_undo_solid,
              save: async (evalu) => {
                return rpc(routes.unlockEvaluationSpec, undefined, {
                  id: evalu.id,
                });
              },
              reload: true,
            })}
          </section>
        )}
      </div>
    );
  } else if (opts.evaluation.tag === "new") {
    const ev = opts.evaluation;
    const form = new Form({})
      .addField("id", [], () => constantField(null))
      .addField("enrollmentid", [], () => constantField(opts.enrollmentid))
      .addField("template", [], async () => {
        return selectBox({
          values: ev.allTemplates,
          initial: null,
          show: (templ) => templ.label,
        });
      })
      .addField("sections", ["template"], ({ template }) => {
        return questionsField<{
          evaluation_template_question_id: evaluation_template_question_id;
        }>(
          (template.questions || [])
            .orderBy((s) => s.ordering)
            .map((q) => ({
              ...q,
              evaluation_template_question_id: q.id,
              number_answer: null,
              text_answer: null,
              boolean_answer: null,
              select_answer: null,
              multiselect_answers: null,
              mandatory: false,
            })),
        );
      })
      .build({
        render: (fields) => (
          <div>
            <div style="display: flex; align-items: flex-end; justify-content: space-between;">
              <label for="">{"Type evaluatie"}</label>
              {opts.userCanEditTemplates ? (
                <a href={routes.evaluation_templates_listRoute.link()}>
                  {"Bekijk evaluatietypes"}
                </a>
              ) : (
                <span></span>
              )}
            </div>
            {fields.template()}
            {fields.sections()}
          </div>
        ),
      });

    return (
      <div>
        {form.render()}

        <section style="text-align: right">
          {renderSaveButton({
            formS: form.s,
            save: async (evalu) => {
              const res = await rpc(routes.saveEvaluationSpec, undefined, {
                id: null,
                enrollmentid: opts.enrollmentid,
                template: evalu.template.id,
                sections: evalu.sections,
              });
              return {
                ...res,
                redirectUrl: routes.evaluation_editRoute.link({
                  enrollmentid: evalu.enrollmentid,
                  id: res.id,
                }),
              };
            },
            reload: false,
          })}
        </section>
      </div>
    );
  } else {
    return checkAllCasesHandled(opts.evaluation);
  }
}
